import { useEffect, useState } from 'react';
import * as React from 'react';

import { statusEnum } from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

import Template from '../Template';
import ConfirmAnswerResultModal from '../Template/Modals/ConfirmAnswerResultModal';
import QuestionnaireModal from '../Template/Modals/QuestionnaireModal';
import { convertAdminFormValue, convertControlFormValue } from '../utils/convertFormValue';
import { genConvertRequestableSearchCondition } from '../utils/convertRequestableSearchCondition';
import { useDeleteQuestionnaire } from '../utils/CustomHooks/useDeleteQuestionnaire';
import { useEditQuestionnaire } from '../utils/CustomHooks/useEditQuestionnaire';
import { useSearchQuestionnaire } from '../utils/CustomHooks/useSearchQuestionnaire';
import { useViewQuestionnaire } from '../utils/CustomHooks/useViewQuestionnaire';
import { convertSubmittableValue } from './utils/convertSubmittableValue';
import { DATABASE_REPLICA } from 'constants/GlobalConfig';

const INIT_SEARCH_CONDITION = {
    name: '',
    page: 1,
    pageSize: 50,
};

type Props = {
    activeTab: 'school' | 'board';
}

const SchoolContents: React.FC<Props> = ({
    activeTab,
}) => {
    const [
        searchQuestionnaire,
        fetchSearchQuestionnaire,
    ] = useSearchQuestionnaire(activeTab);
    const [
        viewQuestionnaire,
        fetchViewQuestionnaire,
        resetViewQuestionnaire,
    ] = useViewQuestionnaire();
    const [
        deleteResult,
        deleteQuestionnaire,
        resetDeleteResult,
    ] = useDeleteQuestionnaire();
    const [
        editQuestionnaire,
        createQuestionnaire,
        updateQuestionnaire,
        resetEditQuestionnaire,
    ] = useEditQuestionnaire();
    const [isCopy, setIsCopy] = useState(false);
    const [searchCondition, setSearchCondition] = useState<EverydayNote.SearchConditions>(INIT_SEARCH_CONDITION);
    const [visibleEditModal, setVisibleEditModal] = useState(false);
    const [visibleResultModal, setVisibleResultModal] = useState(false);
    const [questionnaire, setQuestionnaire] = useState<EverydayNote.AdminQuestionnaire>();
    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);
    const organization = useAppSelector(state => state.user.payload.organization);

    const handleAnswerResultClick = (questionnaire: EverydayNote.AdminQuestionnaire) => {
        setVisibleResultModal(true);
        setQuestionnaire(questionnaire);
    };

    const handleChangeSearchMode = () => {
        setSearchCondition(condition => {
            const { name, page, pageSize } = condition;
            return {
                name,
                page,
                pageSize,
            };
        });
    };

    const handleAnswerResultClose = () => {
        setVisibleResultModal(false);
    };

    const handleCopyClick = (questionnaire: EverydayNote.AdminQuestionnaire) => {
        fetchViewQuestionnaire(questionnaire, 'school');
        setIsCopy(true);
    };

    const handleCreateClick = () => setVisibleEditModal(true);

    const handleDeleteClick = (uuid: string) => {
        deleteQuestionnaire(uuid);
    };

    const handleEditCancel = () => {
        setVisibleEditModal(false);
        setIsCopy(false);
        resetViewQuestionnaire();
    };

    const handleOk = (values: EverydayNote.FormValue) => {
        if('canAdminSetPublic' in values) return;

        // 型推論がうまくいかないので、 submitValue を完全に分離している。
        const submitValue = convertSubmittableValue(values, isCopy);

        if ('uuid' in submitValue) {
            updateQuestionnaire(submitValue, 'school');
        } else {
            createQuestionnaire(submitValue);
        }
    };


    const handleSearch = (condition?: Partial<EverydayNote.SearchConditions>) => {
        const newCondition = {
            ...searchCondition,
            ...condition,
        };

        setSearchCondition(newCondition);
        fetchSearchQuestionnaire(newCondition, 'school');
    };

    const handleUpdateClick = fetchViewQuestionnaire;

    useEffect(() => {
        if (activeTab !== 'school') return;

        handleSearch(searchCondition);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (!viewQuestionnaire.isLoading) return;

        setVisibleEditModal(true);
    }, [viewQuestionnaire.isLoading]);

    useEffect(() => {
        if (!deleteResult.item) return;

        // 「毎日の記録」の削除成功後、サーバーでのレプリカラグを防ぐために1秒間待機する。
        setTimeout(() => {
            handleSearch({ page: 1 });
        }, DATABASE_REPLICA.LAG_TIMEOUT_MS)
        resetDeleteResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteResult, resetDeleteResult]);

    useEffect(() => {
        const result = editQuestionnaire.result;

        if (result?.status !== statusEnum.SUCCESS) return;

        if (result?.isSuccessful) {
            setVisibleEditModal(false);
            setIsCopy(false);
            resetViewQuestionnaire();
            resetEditQuestionnaire();
            handleSearch();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editQuestionnaire]);

    const isModalLoading = viewQuestionnaire.isLoading || editQuestionnaire.isLoading;

    // ここでは convertAdminFormValue しか使われないが、型推論のために色々書いている
    const modalFormValue = React.useMemo(() => {
        if (viewQuestionnaire.isLoading) return undefined;

        return viewQuestionnaire.item && 'canAdminSetPublic' in viewQuestionnaire.item ?
            convertControlFormValue(viewQuestionnaire.item, isCopy) :
            convertAdminFormValue(viewQuestionnaire.item, isCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewQuestionnaire.isLoading, visibleEditModal]);

    const convertRequestableSearchCondition = genConvertRequestableSearchCondition(isControllableUser);

    return (
        <>
            <Template<EverydayNote.AdminQuestionnaire>
                currentPage={searchCondition.page}
                currentTab='school'
                // 一旦、型を適切に表現するのが難しいので型アサーションで対応
                dataSource={searchQuestionnaire.items as EverydayNote.AdminQuestionnaire[]}
                initialPagination={{
                    current: searchCondition.page,
                    pageSize: searchCondition.pageSize,
                }}
                isControllableUser={isControllableUser}
                loading={searchQuestionnaire.isLoading}
                onAnswerResultClick={handleAnswerResultClick}
                onChangeSearchMode={handleChangeSearchMode}
                onCopyClick={handleCopyClick}
                onCreateClick={handleCreateClick}
                onDeleteClick={handleDeleteClick}
                onSearch={handleSearch}
                onUpdateClick={handleUpdateClick}
                totalDataCount={searchQuestionnaire.itemCount}
            />

            <QuestionnaireModal
                isAnswered={isCopy ? false : !!viewQuestionnaire.item?.isLocked}
                isCopy={isCopy}
                loading={isModalLoading}
                onCancel={handleEditCancel}
                onOk={handleOk}
                questionnaire={modalFormValue}
                tabName='school'
                visible={visibleEditModal}
            />

            <ConfirmAnswerResultModal
                convertRequestableSearchCondition={convertRequestableSearchCondition}
                loading={false}
                onClose={handleAnswerResultClose}
                organization={organization}
                questionnaire={questionnaire}
                tabName='school'
                visible={visibleResultModal}
            />
        </>
    );
};

export default SchoolContents;
